/*
## Flash Service

A central authority for presenting transient messages in response to merchant
interaction such as saving and editing. It can also dynamically display messages
from a passed validations object.

### Flash Contexts

There are currently four supported flash contexts: global, sheet, modal, and blade.
You can target them using context-specific helper methods such as `globalError`,
`sheetError`, `modalError`, `bladeError`, etc.

### Managing Validation

You can use `showValidation` and `clearValidation` (and corresponding
context-specific convenience methods) to pass a `validations` object for any
context, except for the blade context, which is meant to be read-only.
This object should have a `message` attribute that contains the current
validation error to display. It can also have a `warningMessage` attribute that contains the
current validation warning to display. The validation object maintained by [Ember CP
Validations](https://github.com/offirgolan/ember-cp-validations) works great for this!

Passing validation to a context will also set the `showingValidation` attribute
for that context to `true`. When using [`sq-form`](/components/glass/sq-form.html), it will use this for the
`showValidation` attribute on its yielded form fields, allowing you to keep
flash and field-level error visibility in sync. Speaking of which...

### Focused Error Messaging

This service also exposes `setFocusedMessage` and `clearFocusedMessage` methods
which can be used to display the error message corresponding to the currently
focused form field. This is used by [`sq-form`](/components/glass/sq-form.html) and [`sq-form-field`](/components/glass/sq-form-field.html).
*/

import EmberObject, { computed } from '@ember/object';
import Service from '@ember/service';
import { notEmpty } from '@ember/object/computed';
import { isPresent } from '@ember/utils';
import BannerFlashComponent from '@square/glass-ui/components/banner-flash';
import BannerFlashGlobalComponent from '@square/glass-ui/components/banner-flash-global';
import { FLASH_TYPES } from '@square/glass-ui/utils/flash-types';
export const FLASH_DISMISS_MS = 5000;

// eslint-disable-next-line ember/no-classic-classes
export const FlashContext = EmberObject.extend({
  defaultFlashComponent: BannerFlashComponent,
  // passed in create
  FLASH_DISMISS_MS: null,
  // Manual messaging
  message: null,
  messageType: null,
  // Dynamic validation messaging
  validations: null,
  showingValidation: computed('validations.{message,warningMessage}', function () {
    return isPresent(this.get('validations.message')) || isPresent(this.get('validations.warningMessage'));
  }),
  // Field Level Errors (shown on focus via sq-form-field)
  focusedMessage: '',
  shouldShowFlash: notEmpty('currentFlashObject'),
  // The message and type for the currently shown flash message. Will be `null` if nothing is actively being shown.
  currentFlashObject: computed('focusedMessage', 'message', 'messageType', 'validations.{message,warningMessage}', function () {
    // Note that we only show focused messages when there are active validation messages,
    // to avoid async issues with validation and focus states.
    if (this.get('focusedMessage') && (this.get('validations.message') || this.get('validations.warningMessage'))) {
      return {
        message: this.get('focusedMessage'),
        type: FLASH_TYPES.ERROR
      };
    }
    if (this.get('message')) {
      return {
        message: this.get('message'),
        type: this.get('messageType')
      };
    }
    if (this.get('validations.message')) {
      return {
        message: this.get('validations.message'),
        type: FLASH_TYPES.ERROR
      };
    }
    if (this.get('validations.warningMessage')) {
      return {
        message: this.get('validations.warningMessage'),
        type: FLASH_TYPES.WARNING
      };
    }
    return null;
  }),
  destroy(...args) {
    clearTimeout(this.autoDismissTask);
    this._super(...args);
  },
  // Messaging - used to manually pass a message for display
  setMessage(message, {
    messageType,
    persistent,
    flashComponent,
    componentClass,
    componentOptions,
    dismiss
  }) {
    clearTimeout(this.autoDismissTask);
    this.setProperties({
      message,
      messageType,
      flashComponent,
      componentClass,
      componentOptions,
      dismiss
    });
    if (persistent !== true) {
      this.scheduleAutoDismiss();
    }
  },
  error(message, options = {}) {
    this.setMessage(message, {
      persistent: true,
      ...options,
      messageType: FLASH_TYPES.ERROR
    });
  },
  warning(message, options = {}) {
    this.setMessage(message, {
      ...options,
      messageType: FLASH_TYPES.WARNING
    });
  },
  success(message, options) {
    this.setMessage(message, {
      ...options,
      messageType: FLASH_TYPES.SUCCESS
    });
  },
  clearMessage() {
    if (this.isDestroyed) {
      return;
    }
    this.setProperties({
      message: null,
      messageType: null,
      componentOptions: {}
    });
  },
  // This is only its own method so it can be stubbed in tests
  scheduleAutoDismiss() {
    this.autoDismissTask = setTimeout(() => this.clearMessage(), FLASH_DISMISS_MS);
  },
  // Validations - used to dynamically display the current validations
  // `validations` can be either a CP Validations object or the one generated
  // by the deprecated `validated_mixin`
  showValidation(validations) {
    this.set('validations', validations);
  },
  // Convert a changeset into a validation
  showChangeset(changeset) {
    this.set('validations', {
      message: changeset.errors[0]?.validation
    });
  },
  clearValidation() {
    this.set('validations', null);
  },
  // Field-level Focused Message
  setFocusedMessage(message) {
    this.set('focusedMessage', message);
  },
  clearFocusedMessage() {
    this.set('focusedMessage', null);
  }
});

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  init(...args) {
    this._super(...args);
    this.set('contexts', {
      global: FlashContext.create({
        defaultFlashComponent: BannerFlashGlobalComponent
      }),
      sheet: FlashContext.create(),
      modal: FlashContext.create(),
      blade: FlashContext.create()
    });
  },
  // Context messaging convenience methods
  globalError(message, options) {
    this.get('contexts.global').error(message, options);
  },
  globalWarning(message, options) {
    this.get('contexts.global').warning(message, options);
  },
  globalSuccess(message, options) {
    this.get('contexts.global').success(message, options);
  },
  sheetError(message, options) {
    this.get('contexts.sheet').error(message, options);
  },
  sheetWarning(message, options) {
    this.get('contexts.sheet').warning(message, options);
  },
  sheetSuccess(message, options) {
    this.get('contexts.sheet').success(message, options);
  },
  modalError(message, options) {
    this.get('contexts.modal').error(message, options);
  },
  modalWarning(message, options) {
    this.get('contexts.modal').warning(message, options);
  },
  modalSuccess(message, options) {
    this.get('contexts.modal').success(message, options);
  },
  bladeError(message, options) {
    this.get('contexts.blade').error(message, options);
  },
  bladeWarning(message, options) {
    this.get('contexts.blade').warning(message, options);
  },
  bladeSuccess(message, options) {
    this.get('contexts.blade').success(message, options);
  },
  clearGlobalMessage() {
    this.get('contexts.global').clearMessage();
  },
  clearSheetMessage() {
    this.get('contexts.sheet').clearMessage();
  },
  clearModalMessage() {
    this.get('contexts.modal').clearMessage();
  },
  clearBladeMessage() {
    this.get('contexts.blade').clearMessage();
  },
  // Context validation convenience methods
  showGlobalValidation(validations) {
    this.get('contexts.global').showValidation(validations);
  },
  clearGlobalValidation() {
    this.get('contexts.global').clearValidation();
  },
  showSheetValidation(validations) {
    this.get('contexts.sheet').showValidation(validations);
  },
  clearSheetValidation() {
    this.get('contexts.sheet').clearValidation();
  },
  showModalValidation(validations) {
    this.get('contexts.modal').showValidation(validations);
  },
  clearModalValidation() {
    this.get('contexts.modal').clearValidation();
  },
  showBladeValidation(validations) {
    this.get('contexts.blade').showValidation(validations);
  },
  clearBladeValidation() {
    this.get('contexts.blade').clearValidation();
  },
  showGlobalChangeset(changeset) {
    this.get('contexts.global').showChangeset(changeset);
  },
  showSheetChangeset(changeset) {
    this.get('contexts.sheet').showChangeset(changeset);
  },
  showModalChangeset(changeset) {
    this.get('contexts.modal').showChangeset(changeset);
  },
  showBladeChangeset(changeset) {
    this.get('contexts.blade').showChangeset(changeset);
  }
});